import { useEffect, useMemo } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeft, Sparkles, MapPin, Briefcase, Users, ShieldCheck, Clock, Bookmark } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { useJobDetailQuery, useSimilarJobsQuery } from '../../hooks/useJobDetail';
import { Helmet } from 'react-helmet-async';
import { toast } from 'sonner';
import posthog from 'posthog-js';
import { recordJobAnalytics } from '../../api/jobAnalytics';
import { isEmployerVerified } from '../../lib/jobVerification';

type EmployerLike = {
  id?: string | number;
  name?: string;
  company_name?: string;
  companyId?: number;
  company_id?: number;
  slug?: string;
  website?: string;
  description?: string;
  industry?: string;
  location?: string;
  employees?: string | number;
};

const siteUrl = import.meta.env.VITE_SITE_URL?.replace(/\/$/, '');

const parseSalaryRange = (salary?: string | null) => {
  if (!salary) return null;
  const matches = salary.match(/\d+(?:[.,]\d+)?/g);
  if (!matches) return null;
  const numbers = matches
    .map((value) => Number.parseInt(value.replace(/[^\d]/g, ''), 10))
    .filter((value) => !Number.isNaN(value));
  if (!numbers.length) return null;
  return {
    min: Math.min(...numbers),
    max: Math.max(...numbers),
    currency: salary.includes('$') ? 'USD' : 'GBP',
  };
};

const formatCurrency = (value?: number | null, currency = 'GBP') => {
  if (typeof value !== 'number' || Number.isNaN(value)) return null;
  return new Intl.NumberFormat('en-GB', {
    style: 'currency',
    currency,
    maximumFractionDigits: 0,
  }).format(value);
};

export function JobDetail() {
  const { jobSlug: jobSlugParam } = useParams<{ jobSlug: string }>();
  const {
    data: job,
    isPending,
  } = useJobDetailQuery(jobSlugParam);
  const {
    data: similarJobs = [],
    isPending: similarPending,
    isError: similarError,
    refetch: refetchSimilar,
  } = useSimilarJobsQuery(jobSlugParam, Boolean(job));

  const company = (job as { employer?: EmployerLike } | undefined)?.employer;
  const verifiedEmployer = isEmployerVerified(job) || isEmployerVerified(company);

  const relatedJobs = useMemo(() => {
    if (!job) return [];
    if (!similarPending && similarJobs.length) {
      return similarJobs;
    }
    return [];
  }, [job, similarJobs, similarPending]);

  const jobSlug = (job as { slug?: string })?.slug ?? job?.id ?? jobSlugParam ?? 'role';
  const sanitizedHtml = (job as { sanitizedHtml?: string | null })?.sanitizedHtml ?? null;
  const overviewText = sanitizedHtml ? stripHtml(sanitizedHtml) : job?.description;
  const postedDateRaw = (job as { postedDate?: string })?.postedDate ?? job?.posted;
  const postedLabel = postedDateRaw ? formatPostedDate(postedDateRaw) : job?.posted ?? 'Recently posted';
  const occupationLabel =
    (job as { occupation?: { preferredLabel?: string } })?.occupation?.preferredLabel ??
    (job as { occupationTitle?: string | null })?.occupationTitle ??
    (job as { occupationId?: string | null })?.occupationId;
  const canonicalUrl = useMemo(() => {
    if (siteUrl) return `${siteUrl}/jobs/${jobSlug}`;
    if (typeof window !== 'undefined') {
      return `${window.location.origin}/jobs/${jobSlug}`;
    }
    return `https://jobs.lighthouse.careers/jobs/${jobSlug}`;
  }, [jobSlug]);

  const employerName = resolveCompanyName(company);
  const salaryRange = parseSalaryRange(job?.salary);
  const metaDescription =
    overviewText?.slice(0, 155) ?? `Explore roles from Lighthouse returner-friendly employers.`;

  const jobMidpoint = salaryRange ? Math.round((salaryRange.min + salaryRange.max) / 2) : null;
  const salaryInsights = (job as {
    salaryInsights?: {
      median?: number;
      percentile50?: number;
      percentile75?: number;
      percentile25?: number;
      source?: string;
      currency?: string;
    };
  })?.salaryInsights;
  const marketMedian =
    salaryInsights?.median ?? salaryInsights?.percentile50 ?? (jobMidpoint ?? undefined);
  const percentile75 =
    salaryInsights?.percentile75 ?? (marketMedian ? Math.round(marketMedian * 1.1) : null);
  const percentile25 =
    salaryInsights?.percentile25 ?? (marketMedian ? Math.round(marketMedian * 0.9) : null);
  const insightCurrency = salaryInsights?.currency ?? salaryRange?.currency ?? 'GBP';
  const salaryDelta = jobMidpoint && marketMedian ? jobMidpoint - marketMedian : null;
  const midpointPercent =
    percentile25 && percentile75 && jobMidpoint
      ? Math.min(
          100,
          Math.max(0, ((jobMidpoint - percentile25) / (percentile75 - percentile25 || 1)) * 100),
        )
      : null;

  const jobPostingJsonLd = useMemo(() => {
    if (!job) return null;
    const postedProp = (job as { postedDate?: string }).postedDate ?? job?.posted;
    const postedDate =
      postedProp && !Number.isNaN(Date.parse(postedProp)) ? new Date(postedProp).toISOString() : new Date().toISOString();
    const payload: Record<string, unknown> = {
      '@context': 'https://schema.org',
      '@type': 'JobPosting',
      title: job.title,
      description: overviewText ?? job.description,
      datePosted: postedDate,
      employmentType: job.type,
      industry: job.category,
      jobLocation: {
        '@type': 'Place',
        address: job.location,
      },
      hiringOrganization: {
        '@type': 'Organization',
        name: employerName,
        sameAs: company?.website,
      },
      jobBenefits: job.benefits,
      applicantLocationRequirements: job.remote ? 'Remote' : job.location,
      url: canonicalUrl,
    };
    if (salaryRange) {
      payload.baseSalary = {
        '@type': 'MonetaryAmount',
        currency: salaryRange.currency,
        value: {
          '@type': 'QuantitativeValue',
          minValue: salaryRange.min,
          maxValue: salaryRange.max,
          unitText: 'YEAR',
        },
      };
    }
    return payload;
  }, [job, employerName, canonicalUrl, company, salaryRange, overviewText]);

  const applyDestination =
    (job as { applyUrl?: string })?.applyUrl ??
    (job as { applicationUrl?: string })?.applicationUrl ??
    (company as { website?: string })?.website;

  useEffect(() => {
    if (!job?.id) return;
    void recordJobAnalytics(job.id, 'view');
  }, [job?.id]);

  const handleApply = () => {
    if (!job) return;
    void recordJobAnalytics(job.id, 'apply');
    posthog.capture('job_detail_apply_clicked', { jobId: job.id, jobSlug, hasExternalUrl: Boolean(applyDestination) });
    if (applyDestination && typeof window !== 'undefined') {
      window.open(applyDestination, '_blank', 'noopener');
    } else {
      toast.success('Application instructions have been emailed to you.');
    }
  };

  const handleSave = () => {
    if (!job) return;
    posthog.capture('job_detail_save_clicked', { jobId: job.id, jobSlug });
    toast.success('Saved to your list. Visit the Saved Jobs tab to review later.');
  };

  if (isPending) {
    return <JobDetailSkeleton />;
  }

  if (!job) {
    return (
      <div className="mx-auto max-w-3xl px-4 py-16 sm:px-6 lg:px-8">
        <Card className="border-white/10 bg-white/[0.02]">
          <CardContent className="space-y-4 p-8 text-center">
            <p className="text-2xl font-semibold text-white">Role unavailable</p>
            <p className="text-sm text-gray-300">
              We couldn&apos;t find the job you were looking for.
              {jobSlugParam
                ? ` The role “${jobSlugParam}” may have been filled or removed by the employer.`
                : ' It may have been filled or removed by the employer.'}
            </p>
            <Button asChild className="bg-gold-gradient text-black hover:brightness-110">
              <Link to="/jobs">Browse other roles</Link>
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>{`${job.title} | Lighthouse Jobs`}</title>
        <meta name="description" content={metaDescription} />
        <link rel="canonical" href={canonicalUrl} />
        <meta property="og:title" content={`${job.title} | Lighthouse`} />
        <meta property="og:description" content={metaDescription} />
        <meta property="og:url" content={canonicalUrl} />
        <meta property="og:type" content="website" />
        <meta name="twitter:card" content="summary_large_image" />
        {jobPostingJsonLd && <script type="application/ld+json">{JSON.stringify(jobPostingJsonLd)}</script>}
      </Helmet>
      <main aria-labelledby="job-detail-heading" className="min-h-screen bg-[#0a0c1c] pb-20">
        <div className="mx-auto max-w-5xl px-4 py-10 sm:px-6 lg:px-8">
        <Link to="/jobs" className="inline-flex items-center gap-2 text-sm text-gray-300 hover:text-[#f7ef8a]">
          <ArrowLeft className="h-4 w-4" aria-hidden />
          Back to all jobs
        </Link>

        <article className="mt-6 grid gap-8 lg:grid-cols-[minmax(0,3fr)_minmax(0,2fr)]">
          <Card className="border-white/10 bg-white/[0.03]">
            <CardHeader className="space-y-4">
              <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
                <div>
                  <Badge className="mb-3 bg-gold-gradient text-[#121321]">
                    {job.category}
                  </Badge>
                  <CardTitle id="job-detail-heading" className="text-2xl text-white">{job.title}</CardTitle>
                  <div className="mt-3 flex flex-wrap items-center gap-3 text-sm text-gray-300">
                    {company && (
                      <Link to={`/company/${resolveCompanySlug(company)}`} className="flex items-center gap-1 hover:text-[#f7ef8a]">
                        <Users className="h-4 w-4 text-[#ba9036]" aria-hidden />
                        {company.name}
                      </Link>
                    )}
                    <span className="flex items-center gap-1">
                      <MapPin className="h-4 w-4 text-[#ba9036]" aria-hidden />
                      {job.location}
                    </span>
                    <span className="flex items-center gap-1">
                      <Briefcase className="h-4 w-4 text-[#ba9036]" aria-hidden />
                      {job.type}
                    </span>
                    {occupationLabel && (
                      <span className="flex items-center gap-1 rounded-full border border-white/10 bg-white/5 px-2 py-1 text-xs text-gray-200">
                        Occupation: {occupationLabel}
                      </span>
                    )}
                    {job.returnerFriendly && (
                      <span className="flex items-center gap-1 text-[#f7ef8a]">
                        <Sparkles className="h-4 w-4" aria-hidden />
                        Returner friendly
                      </span>
                    )}
                  </div>
                </div>
                <div className="flex flex-col items-start gap-3 rounded-xl border border-white/10 bg-white/[0.02] p-4 md:items-end">
                  <span className="text-xs uppercase tracking-widest text-gray-400">Salary</span>
                  <span className="text-xl font-semibold text-white">{job.salary}</span>
                  <span className="text-sm text-gray-400">Posted {job.posted}</span>
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-8 p-8 pt-0">
              <section className="space-y-4">
                <h2 className="text-lg font-semibold text-white">Overview</h2>
                {sanitizedHtml ? (
                  <article
                    className="prose prose-invert max-w-none text-sm leading-6 text-gray-200 prose-a:text-[#f7ef8a] prose-strong:text-white prose-headings:text-white"
                    dangerouslySetInnerHTML={{ __html: sanitizedHtml }}
                  />
                ) : (
                  <p className="text-sm leading-6 text-gray-300">
                    {overviewText ?? 'This employer has not provided a public description for this role yet.'}
                  </p>
                )}
              </section>

              <section className="rounded-2xl border border-white/10 bg-white/[0.02] p-5 text-sm text-gray-200">
                <div className="flex flex-wrap items-start justify-between gap-4">
                  <div>
                    <p className="text-xs uppercase tracking-[0.3em] text-gray-500">Salary insights</p>
                    <p className="text-base font-semibold text-white">{job.salary || 'Competitive'}</p>
                    <p className="text-xs text-gray-400">
                      Range shared by employer. {!salaryRange && 'Contact the hiring manager for more detail.'}
                    </p>
                  </div>
                  {salaryDelta !== null && (
                    <Badge
                      className={`border ${
                        salaryDelta >= 0
                          ? 'border-emerald-400/40 bg-emerald-500/10 text-emerald-200'
                          : 'border-red-400/40 bg-red-500/10 text-red-200'
                      }`}
                    >
                      {salaryDelta >= 0 ? 'Above market' : 'Below market'}
                    </Badge>
                  )}
                </div>
                <div className="mt-4 grid gap-4 md:grid-cols-3">
                  <div>
                    <p className="text-xs text-gray-400">Estimated range</p>
                    <p className="text-base text-white">
                      {salaryRange
                        ? `${formatCurrency(salaryRange.min, insightCurrency)} - ${formatCurrency(salaryRange.max, insightCurrency)}`
                        : job.salary ?? 'Not disclosed'}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-400">Market median</p>
                    <p className="text-base text-white">
                      {marketMedian ? formatCurrency(marketMedian, insightCurrency) : 'N/A'}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-400">Delta vs market</p>
                    <p className="text-base text-white">
                      {salaryDelta !== null && marketMedian
                        ? `${salaryDelta >= 0 ? '+' : '-'}${formatCurrency(Math.abs(salaryDelta), insightCurrency)}`
                        : 'Aligned'}
                    </p>
                  </div>
                </div>
                {midpointPercent !== null && percentile25 && percentile75 && (
                  <div className="mt-4">
                    <div className="flex items-center justify-between text-xs text-gray-400">
                      <span>{formatCurrency(percentile25, insightCurrency)} (25th)</span>
                      <span>{formatCurrency(percentile75, insightCurrency)} (75th)</span>
                    </div>
                    <div className="relative mt-2 h-2 rounded-full bg-white/5">
                      <div
                        className="absolute inset-y-0 left-0 rounded-full bg-gold-gradient"
                        style={{ width: `${midpointPercent}%` }}
                      />
                      <div
                        className="absolute top-1/2 h-3 w-3 -translate-y-1/2 rounded-full border border-black/20 bg-white shadow-lg shadow-black/30"
                        style={{ left: `calc(${midpointPercent}% - 6px)` }}
                        aria-hidden
                      />
                    </div>
                    <p className="mt-2 text-xs text-gray-400">
                      Source: {salaryInsights?.source ?? 'Lighthouse benchmark across verified employers'}
                    </p>
                  </div>
                )}
              </section>

              <section className="space-y-3">
                <h3 className="text-base font-semibold text-white">Requirements</h3>
                <ul className="space-y-2 text-sm text-gray-300">
                  {(job.requirements ?? []).map((item) => (
                    <li key={item} className="flex items-start gap-2">
                      <span className="mt-1 inline-block h-2.5 w-2.5 rounded-full bg-[#f7ef8a]" aria-hidden />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </section>

              <section className="space-y-3">
                <h3 className="text-base font-semibold text-white">Benefits</h3>
                <ul className="flex flex-wrap gap-2">
                  {(job.benefits ?? []).map((benefit) => (
                    <Badge key={benefit} className="border-white/10 bg-white/[0.08] text-gray-100">
                      {benefit}
                    </Badge>
                  ))}
                </ul>
              </section>

              <section className="space-y-3">
                <h3 className="text-base font-semibold text-white">What Lighthouse checks for</h3>
                <p className="text-sm text-gray-300">
                  Every listing is moderated. When an employer is verified, they commit to inclusive interviewing and
                  structured feedback.
                </p>
                <div className="flex flex-wrap gap-3 text-sm text-gray-300">
                  {verifiedEmployer && (
                    <span className="flex items-center gap-2 rounded-full border border-[#d2ac47]/30 px-3 py-1">
                      <ShieldCheck className="h-4 w-4 text-[#ba9036]" aria-hidden />
                      Verified employer
                    </span>
                  )}
                  <span className="flex items-center gap-2 rounded-full border border-[#d2ac47]/30 px-3 py-1">
                    <Clock className="h-4 w-4 text-[#ba9036]" aria-hidden />
                    Avg. response 3-5 days
                  </span>
                  <span className="flex items-center gap-2 rounded-full border border-[#d2ac47]/30 px-3 py-1">
                    <Bookmark className="h-4 w-4 text-[#ba9036]" aria-hidden />
                    {job.applicants} applicants so far
                  </span>
                </div>
              </section>
            </CardContent>
          </Card>

          <aside className="space-y-6" aria-label="Application actions and related jobs">
            <Card className="border-white/10 bg-white/[0.03]">
              <CardContent className="space-y-4 p-6">
                 <Button className="w-full bg-gold-gradient text-black hover:brightness-110" onClick={handleApply}>
                   Apply now
                 </Button>
                 <Button variant="outline" className="w-full border-white/20 text-white hover:bg-white/10" onClick={handleSave}>
                   Save for later
                 </Button>
                <p className="text-xs text-gray-400">
                  Applying through Lighthouse means your profile is shared directly with the hiring team. We’ll keep you updated as the status changes.
                </p>
              </CardContent>
            </Card>

            {company && (
              <Card className="border-white/10 bg-white/[0.03]">
                <CardContent className="space-y-4 p-6">
                  <div className="space-y-2">
                    <h2 className="text-lg font-semibold text-white">{resolveCompanyName(company)}</h2>
                    {'description' in company && company.description && (
                      <p className="text-sm text-gray-300">{company.description}</p>
                    )}
                  </div>
                  <div className="flex flex-wrap gap-2 text-xs text-gray-300">
                    {'industry' in company && company.industry && (
                      <span className="rounded-full border border-white/10 px-3 py-1">{company.industry}</span>
                    )}
                    {'location' in company && company.location && (
                      <span className="rounded-full border border-white/10 px-3 py-1">{company.location}</span>
                    )}
                    {'employees' in company && company.employees && (
                      <span className="rounded-full border border-white/10 px-3 py-1">{company.employees} employees</span>
                    )}
                  </div>
                  {resolveCompanyId(company) && (
                    <Button asChild variant="outline" className="w-full border-[#d2ac47]/40 text-[#f7ef8a] hover:bg-[#ba9036]/10">
                      <Link to={`/company/${resolveCompanySlug(company)}`}>View company profile</Link>
                    </Button>
                  )}
                </CardContent>
              </Card>
            )}

            {relatedJobs.length > 0 && (
              <Card className="border-white/10 bg-white/[0.03]">
                <CardHeader>
                  <CardTitle className="text-lg text-white">More from this employer</CardTitle>
                  {similarError && (
                    <p className="text-xs text-red-300">
                      We couldn&apos;t fetch similar jobs right now. Showing featured roles from this company instead.
                    </p>
                  )}
                </CardHeader>
                <CardContent className="space-y-4">
                  {relatedJobs.map((related) => (
                    <div key={related.id ?? (related as { slug?: string }).slug ?? related.title} className="rounded-lg border border-white/10 bg-white/[0.04] p-4">
                      <Link
                        to={`/jobs/${(related as { slug?: string }).slug ?? related.id ?? related.title}`}
                        className="text-sm font-semibold text-white hover:text-[#f7ef8a]">
                        {related.title}
                      </Link>
                      <p className="mt-1 text-xs text-gray-300">{related.location}</p>
                      <p className="text-xs text-gray-400">{related.type}</p>
                    </div>
                  ))}
                </CardContent>
              </Card>
            )}
            {relatedJobs.length === 0 && similarError && (
              <div className="rounded-lg border border-red-500/30 bg-red-500/10 p-4 text-xs text-red-100">
                Similar jobs are unavailable right now.{' '}
                <button
                  type="button"
                  onClick={() => refetchSimilar()}
                  className="underline decoration-dotted underline-offset-2"
                >
                  Retry
                </button>
              </div>
            )}
          </aside>
        </article>
      </div>
      </main>
    </>
  );
}

function JobDetailSkeleton() {
  return (
    <div className="min-h-screen bg-[#0a0c1c] pb-20">
      <div className="mx-auto max-w-5xl px-4 py-10 sm:px-6 lg:px-8 space-y-6">
        <div className="h-4 w-32 animate-pulse rounded bg-white/10" />
        <div className="grid gap-8 lg:grid-cols-[minmax(0,3fr)_minmax(0,2fr)]">
          <Card className="border-white/10 bg-white/[0.03]">
            <CardHeader>
              <div className="h-8 w-2/3 animate-pulse rounded bg-white/10" />
            </CardHeader>
            <CardContent className="space-y-4">
              {Array.from({ length: 4 }).map((_, index) => (
                <div key={`skeleton-section-${index}`} className="space-y-3">
                  <div className="h-4 w-1/3 animate-pulse rounded bg-white/10" />
                  <div className="space-y-2">
                    <div className="h-3 w-full animate-pulse rounded bg-white/10" />
                    <div className="h-3 w-11/12 animate-pulse rounded bg-white/10" />
                  </div>
                </div>
              ))}
            </CardContent>
          </Card>
          <Card className="border-white/10 bg-white/[0.03]">
            <CardContent className="space-y-4">
              {Array.from({ length: 3 }).map((_, index) => (
                <div key={`skeleton-aside-${index}`} className="h-4 w-full animate-pulse rounded bg-white/10" />
              ))}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}

function resolveCompanyName(company?: EmployerLike) {
  if (!company) return 'Employer';
  return company.name ?? company.company_name ?? 'Employer';
}

function resolveCompanyId(company?: EmployerLike) {
  if (!company) return undefined;
  return (
    company.id ??
    company.companyId ??
    company.company_id
  );
}

function resolveCompanySlug(company?: EmployerLike) {
  if (!company) return undefined;
  return (company as { slug?: string })?.slug ?? resolveCompanyId(company);
}

function stripHtml(value?: string | null) {
  if (!value) return '';
  return value.replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim();
}

function formatPostedDate(value: string | number) {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return typeof value === 'string' ? value : 'Recently posted';
  }
  const now = Date.now();
  const diffMs = now - date.getTime();
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
  if (diffDays <= 0) return 'Today';
  if (diffDays === 1) return '1 day ago';
  if (diffDays < 7) return `${diffDays} days ago`;
  const diffWeeks = Math.floor(diffDays / 7);
  if (diffWeeks < 5) return `${diffWeeks} wk${diffWeeks > 1 ? 's' : ''} ago`;
  return date.toLocaleDateString('en-GB', { year: 'numeric', month: 'short', day: 'numeric' });
}
